/**
 * Парк техники (ТХ-01): карточки единиц с документами и сроками, состояние, закреплённый машинист; новая
 * единица проверяет лимит тарифа. Документы со сроком подсвечиваются за 30 дней.
 */
import {
  UNIT_DOC_KINDS,
  UNIT_KINDS,
  UNIT_STATES,
  type UnitDocKind,
  type UnitDocRow,
  type UnitKind,
  type UnitRow,
  type UnitState,
} from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { api } from '../../lib/api';
import { Button, IconButton } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, EmptyState, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Input, Select, Textarea } from '../../ui/form';
import { Panel } from '../../ui/layout';
import { LField } from '../schedule/LField';
import { DocsBadge, dd, money, n1, num, useFleetRights, useMoney, useUnits } from './shared';

const STATE_TONE: Record<UnitState, 'success' | 'warning' | 'danger' | 'neutral' | 'info'> = {
  working: 'success',
  maintenance: 'warning',
  repair: 'danger',
  idle: 'neutral',
  transit: 'info',
};
const DOC_TONE = { expired: 'danger', soon: 'warning', ok: 'success', open: 'neutral' } as const;

export function FleetPark({ projectId, open }: { projectId: string | undefined; open?: string }) {
  const q = useUnits(projectId);
  const rights = useFleetRights();
  const [edit, setEdit] = useState<string | 'new' | null>(open ?? null);
  const selected =
    typeof edit === 'string' && edit !== 'new' ? q.data?.find((u) => u.machineId === edit) : null;
  return (
    <>
      <Panel
        title="Парк техники"
        meta={projectId ? 'на проекте' : 'вся техника'}
        actions={
          rights.anyEdit ? (
            <Button variant="primary" icon={<Plus className="size-4" />} onClick={() => setEdit('new')}>
              Новая единица
            </Button>
          ) : null
        }
      >
        {q.isPending ? <Spinner /> : null}
        {q.isError ? <ErrorBox error={q.error} /> : null}
        {q.data && !q.data.length ? (
          <EmptyState title="Техники пока нет">
            Добавьте единицу парка: вид, модель, госномер, договор аренды, страховку и техосмотр со сроками.
          </EmptyState>
        ) : null}
        <ul className="divide-y divide-line">
          {(q.data ?? []).map((u) => (
            <li key={u.machineId}>
              <button
                type="button"
                onClick={() => setEdit(u.machineId)}
                className="flex min-h-14 w-full flex-wrap items-center justify-between gap-2 py-2 text-left hover:underline"
              >
                <span className="min-w-0">
                  <span className="block font-medium [overflow-wrap:anywhere]">
                    {u.name}
                    {u.regNumber ? ` · ${u.regNumber}` : ''}
                  </span>
                  <span className="block text-[12px] text-ink-2">
                    {UNIT_KINDS[u.kind]}
                    {u.model ? ` ${u.model}` : ''}
                    {u.year ? `, ${u.year}` : ''}
                    {u.projectCode ? ` · ${u.projectCode}` : ' · не на проекте'}
                    {u.operatorName ? ` · ${u.operatorName}` : ''}
                    {u.hourMeter != null ? ` · ${n1(u.hourMeter)} моточасов` : ''}
                  </span>
                </span>
                <span className="flex flex-wrap items-center gap-1">
                  <DocsBadge state={u.docsState} />
                  <Badge tone={STATE_TONE[u.state]}>{UNIT_STATES[u.state]}</Badge>
                </span>
              </button>
            </li>
          ))}
        </ul>
      </Panel>
      {edit ? (
        <UnitDialog
          key={String(edit)}
          unit={selected ?? null}
          projectId={projectId}
          canEdit={rights.canEdit(selected ? selected.projectId : projectId)}
          onClose={() => setEdit(null)}
        />
      ) : null}
    </>
  );
}

function UnitDialog({
  unit,
  projectId,
  canEdit,
  onClose,
}: {
  unit: UnitRow | null;
  projectId: string | undefined;
  canEdit: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const money$ = useMoney();
  const refresh = () => qc.invalidateQueries({ queryKey: ['fleet'] });
  const kinds = useQuery({
    queryKey: ['resources', 'equipment'],
    queryFn: () => api<Array<{ id: string; name: string; kind: string; model: string | null }>>('/resources'),
    enabled: !unit,
  });
  const equipment = (kinds.data ?? []).filter((r) => r.kind === 'equipment');
  const [f, setF] = useState({
    resourceId: '',
    name: unit?.name ?? '',
    regNumber: unit?.regNumber ?? '',
    kind: (unit?.kind ?? 'other') as UnitKind,
    model: unit?.model ?? '',
    year: unit?.year != null ? String(unit.year) : '',
    inventoryNo: unit?.inventoryNo ?? '',
    state: (unit?.state ?? 'working') as UnitState,
    fuelNorm: unit?.fuelNorm != null ? String(unit.fuelNorm) : '',
    fuelIdleNorm: unit?.fuelIdleNorm != null ? String(unit.fuelIdleNorm) : '',
    fuelTolerancePct: String(unit?.fuelTolerancePct ?? 10),
    amortPerHour: unit?.amortPerHour != null ? String(unit.amortPerHour) : '',
    rentPerHour: unit?.rentPerHour != null ? String(unit.rentPerHour) : '',
    operatorPerHour: unit?.operatorPerHour != null ? String(unit.operatorPerHour) : '',
    servicePerHour: unit?.servicePerHour != null ? String(unit.servicePerHour) : '',
    note: unit?.note ?? '',
  });
  const set = (k: keyof typeof f, v: string) => setF((x) => ({ ...x, [k]: v }));
  const card = () => ({
    kind: f.kind,
    model: f.model.trim() || null,
    year: num(f.year),
    inventoryNo: f.inventoryNo.trim() || null,
    state: f.state,
    fuelIdleNorm: num(f.fuelIdleNorm),
    fuelTolerancePct: num(f.fuelTolerancePct) ?? 10,
    ...(money$
      ? {
          amortPerHour: num(f.amortPerHour),
          rentPerHour: num(f.rentPerHour),
          operatorPerHour: num(f.operatorPerHour),
          servicePerHour: num(f.servicePerHour),
        }
      : {}),
    note: f.note.trim() || null,
  });
  const save = useMutation({
    mutationFn: () =>
      unit
        ? api(`/fleet/units/${unit.machineId}`, { method: 'PUT', body: card() })
        : api('/fleet/units', {
            method: 'POST',
            body: {
              ...card(),
              resourceId: f.resourceId,
              name: f.name.trim(),
              regNumber: f.regNumber.trim() || null,
              projectId: projectId ?? null,
              fuelNorm: num(f.fuelNorm),
            },
          }),
    onSuccess: () => {
      toast(unit ? 'Карточка сохранена' : 'Единица добавлена');
      void refresh();
      onClose();
    },
  });
  const field = (label: string, k: keyof typeof f, p: { hint?: string; type?: string } = {}) => (
    <LField label={label} hint={p.hint}>
      <Input
        value={f[k]}
        inputMode={p.type === 'num' ? 'decimal' : undefined}
        disabled={!canEdit}
        onChange={(e) => set(k, e.target.value)}
      />
    </LField>
  );
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={unit ? unit.name : 'Новая единица техники'}
      width="lg"
      footer={
        <>
          <Button onClick={onClose}>Закрыть</Button>
          {canEdit ? (
            <Button
              variant="primary"
              loading={save.isPending}
              disabled={!unit && (!f.resourceId || !f.name.trim())}
              onClick={() => save.mutate()}
            >
              Сохранить
            </Button>
          ) : null}
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {save.isError ? <ErrorBox error={save.error} /> : null}
        {!unit ? (
          <>
            <LField label="Вид техники из справочника ресурсов">
              <Select value={f.resourceId} onChange={(e) => set('resourceId', e.target.value)}>
                <option value="">Выберите вид</option>
                {equipment.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                    {r.model ? ` ${r.model}` : ''}
                  </option>
                ))}
              </Select>
            </LField>
            {field('Название', 'name')}
            {field('Госномер', 'regNumber')}
          </>
        ) : null}
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <LField label="Тип">
            <Select value={f.kind} disabled={!canEdit} onChange={(e) => set('kind', e.target.value)}>
              {Object.entries(UNIT_KINDS).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </Select>
          </LField>
          <LField label="Состояние">
            <Select value={f.state} disabled={!canEdit} onChange={(e) => set('state', e.target.value)}>
              {Object.entries(UNIT_STATES).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </Select>
          </LField>
          {field('Модель', 'model')}
          {field('Год выпуска', 'year', { type: 'num' })}
          {field('Инвентарный номер', 'inventoryNo')}
          {!unit ? field('Норма расхода топлива, л/ч', 'fuelNorm', { type: 'num' }) : null}
          {field('Расход на холостом ходу, л/ч', 'fuelIdleNorm', { type: 'num' })}
          {field('Допуск расхода сверх нормы, %', 'fuelTolerancePct', {
            type: 'num',
            hint: 'Сверх допуска — поручение механику и руководителю проекта',
          })}
        </div>
        {money$ ? (
          <fieldset className="rounded-md border border-line p-3">
            <legend className="px-1 text-[13px] font-medium">Стоимость машино-часа по плану, ₸/ч</legend>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {field('Амортизация', 'amortPerHour', { type: 'num' })}
              {field('Аренда', 'rentPerHour', { type: 'num' })}
              {field('Машинист', 'operatorPerHour', { type: 'num' })}
              {field('ТО и ремонты', 'servicePerHour', { type: 'num' })}
            </div>
          </fieldset>
        ) : null}
        <LField label="Заметки">
          <Textarea value={f.note} disabled={!canEdit} onChange={(e) => set('note', e.target.value)} />
        </LField>
        {unit ? <DocsList unit={unit} canEdit={canEdit} /> : null}
      </div>
    </Dialog>
  );
}

function DocsList({ unit, canEdit }: { unit: UnitRow; canEdit: boolean }) {
  const qc = useQueryClient();
  const refresh = () => qc.invalidateQueries({ queryKey: ['fleet'] });
  const [d, setD] = useState({ kind: 'insurance' as UnitDocKind, number: '', validTo: '' });
  const add = useMutation({
    mutationFn: () =>
      api(`/fleet/units/${unit.machineId}/docs`, {
        method: 'POST',
        body: { kind: d.kind, number: d.number.trim() || null, validTo: d.validTo || null },
      }),
    onSuccess: () => {
      setD({ kind: 'insurance', number: '', validTo: '' });
      void refresh();
    },
  });
  const del = useMutation({
    mutationFn: (id: string) => api(`/fleet/docs/${id}`, { method: 'DELETE' }),
    onSuccess: () => void refresh(),
  });
  return (
    <section className="rounded-md border border-line p-3">
      <h3 className="mb-2 text-[13px] font-semibold">Документы со сроками</h3>
      {!unit.docs.length ? <p className="text-[13px] text-ink-2">Документов нет.</p> : null}
      <ul className="divide-y divide-line">
        {unit.docs.map((x: UnitDocRow) => (
          <li key={x.id} className="flex flex-wrap items-center justify-between gap-2 py-1.5 text-[14px]">
            <span>
              {UNIT_DOC_KINDS[x.kind]}
              {x.number ? ` № ${x.number}` : ''}
              <span className="block text-[12px] text-ink-2">
                {x.validTo ? `до ${dd(x.validTo)}` : 'без срока'}
              </span>
            </span>
            <span className="flex items-center gap-1">
              <Badge tone={DOC_TONE[x.state]}>
                {x.state === 'expired'
                  ? 'просрочен'
                  : x.state === 'open'
                    ? 'без срока'
                    : `осталось ${x.daysLeft} дн.`}
              </Badge>
              {canEdit ? (
                <IconButton label="Удалить документ" onClick={() => del.mutate(x.id)}>
                  <Trash2 className="size-4" />
                </IconButton>
              ) : null}
            </span>
          </li>
        ))}
      </ul>
      {canEdit ? (
        <div className="mt-3 grid grid-cols-1 gap-2 md:grid-cols-[1fr_1fr_1fr_auto] md:items-end">
          <LField label="Вид">
            <Select
              value={d.kind}
              onChange={(e) => setD((x) => ({ ...x, kind: e.target.value as UnitDocKind }))}
            >
              {Object.entries(UNIT_DOC_KINDS).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </Select>
          </LField>
          <LField label="Номер">
            <Input value={d.number} onChange={(e) => setD((x) => ({ ...x, number: e.target.value }))} />
          </LField>
          <LField label="Действует до">
            <Input
              type="date"
              value={d.validTo}
              onChange={(e) => setD((x) => ({ ...x, validTo: e.target.value }))}
            />
          </LField>
          <Button loading={add.isPending} onClick={() => add.mutate()}>
            Добавить
          </Button>
        </div>
      ) : null}
      {add.isError ? <ErrorBox error={add.error} className="mt-2" /> : null}
      <PlanHint unit={unit} />
    </section>
  );
}

/** Стоимость часа в карточке — только тем, кто видит деньги; остальным подсказка не нужна */
function PlanHint({ unit }: { unit: UnitRow }) {
  const parts = [unit.amortPerHour, unit.rentPerHour, unit.operatorPerHour, unit.servicePerHour];
  if (parts.every((x) => x == null)) return null;
  const sum = parts.reduce<number>((s, x) => s + (x ?? 0), 0);
  return <p className="mt-2 text-[12px] text-ink-2">План часа по карточке: {money(sum)}</p>;
}
